export interface DeadLetterSummary {
  id: number;
  originalTopic: string | null;
  pubsubTopic: string;
  tenantName: string | null;
  eventId: string | null;
  objectId: string | null;
  topic: string | null;
  receivedAt: Date;
  payloadSizeBytes: number;
}

export interface DeadLetterDetail extends DeadLetterSummary {
  payload: unknown;
  attributes: { [key: string]: string } | null;
}

/**
 * The pubsub-ingester-service list endpoint returns a slim page shape, not a
 * Spring Data `Page`, so it gets its own type instead of `core/page`.
 */
export interface DeadLetterPage {
  content: DeadLetterSummary[];
  page: number;
  size: number;
  totalElements: number;
}

export interface DeadLetterFilters {
  originalTopic: string;
  tenantName: string;
  eventId: string;
  objectId: string;
  q: string;
  /** `datetime-local` input value, interpreted as UTC. */
  from: string;
  /** `datetime-local` input value, interpreted as UTC. */
  to: string;
}

export const EMPTY_DEAD_LETTER_FILTERS: DeadLetterFilters = {
  originalTopic: "",
  tenantName: "",
  eventId: "",
  objectId: "",
  q: "",
  from: "",
  to: "",
};

export function DeadLetterSummaryFromJSON(json: any): DeadLetterSummary {
  return {
    id: json["id"],
    originalTopic: json["originalTopic"] ?? null,
    pubsubTopic: json["pubsubTopic"],
    tenantName: json["tenantName"] ?? null,
    eventId: json["eventId"] ?? null,
    objectId: json["objectId"] ?? null,
    topic: json["topic"] ?? null,
    receivedAt: new Date(json["receivedAt"]),
    payloadSizeBytes: json["payloadSizeBytes"],
  };
}

export function DeadLetterDetailFromJSON(json: any): DeadLetterDetail {
  return {
    ...DeadLetterSummaryFromJSON(json),
    payload: json["payload"] ?? null,
    attributes: json["attributes"] ?? null,
  };
}

export function DeadLetterPageFromJSON(json: any): DeadLetterPage {
  return {
    content: (json["content"] ?? []).map(DeadLetterSummaryFromJSON),
    page: json["page"],
    size: json["size"],
    totalElements: json["totalElements"],
  };
}

/** Formats an instant as `yyyy-MM-dd HH:mm:ss UTC`, independent of the browser timezone. */
export function formatUtc(date: Date | null | undefined): string {
  if (!date || isNaN(date.getTime())) {
    return "";
  }
  return date.toISOString().replace("T", " ").slice(0, 19) + " UTC";
}

/** Turns a `datetime-local` value (no zone) into an ISO-8601 UTC instant. */
export function localInputToUtcInstant(value: string): string | undefined {
  if (!value) {
    return undefined;
  }
  const withSeconds = value.length === 16 ? `${value}:00` : value;
  const date = new Date(`${withSeconds}Z`);
  return isNaN(date.getTime()) ? undefined : date.toISOString();
}
